import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// =====================================================
// AUTH API
// =====================================================
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  clientSignup: (signupData) => api.post('/auth/client-signup', signupData),
  agencySignup: (signupData) => api.post('/auth/agency-signup', signupData),
  getCurrentUser: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
};

// =====================================================
// CLIENTS API
// =====================================================
export const clientsAPI = {
  getAll: () => api.get('/clients'),
  getById: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.patch(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  getPosts: (id, params) => api.get(`/clients/${id}/posts`, { params }),
  getArchived: () => api.get('/clients/archived'),
  archive: (id) => api.post(`/clients/${id}/archive`),
  unarchive: (id) => api.post(`/clients/${id}/unarchive`),
};

// =====================================================
// SEO API
// =====================================================
export const seoAPI = {
  getPlansByClient: (clientId) => api.get(`/seo/clients/${clientId}/plans`),
  createPlan: (clientId, data) => api.post(`/seo/clients/${clientId}/plans`, data),
  getKeywordsByPlan: (planId) => api.get(`/seo/plans/${planId}/keywords`),
  addKeyword: (planId, data) => api.post(`/seo/plans/${planId}/keywords`, data),
  updateKeyword: (id, data) => api.patch(`/seo/keywords/${id}`, data),
  generateShareLink: (planId) => api.post(`/seo/plans/${planId}/generate-share-link`),
};

// =====================================================
// POSTS API
// =====================================================
export const postsAPI = {
  getAll: (params) => api.get('/posts', { params }),
  getById: (id) => api.get(`/posts/${id}`),
  create: (data) => api.post('/posts', data),
  update: (id, data) => api.patch(`/posts/${id}`, data),
  delete: (id) => api.delete(`/posts/${id}`),
  approve: (id) => api.post(`/posts/${id}/approve`),
  reject: (id, data) => api.post(`/posts/${id}/reject`, data),
  
  // Versions
  getVersions: (id) => api.get(`/posts/${id}/versions`),
  createVersion: (id, data) => api.post(`/posts/${id}/versions`, data),
  
  // Share link
  generateShareLink: (id) => api.post(`/posts/${id}/generate-share-link`),
  
  // Trash bin
  getDeleted: (params) => api.get('/posts/deleted', { params }),
  restore: (id) => api.post(`/posts/${id}/restore`),
  permanentlyDeleteOld: () => api.delete('/posts/permanent-delete-old'),
  
  // Import
  importPosts: (postsData) => api.post('/posts/import', { posts: postsData }),
  
  // Instagram sharing
  shareToInstagram: (id, mediaType = 'IMAGE') => api.post(`/posts/${id}/share-instagram`, { media_type: mediaType }),
};

// =====================================================
// AI API
// =====================================================
export const aiAPI = {
  checkAvailable: () => api.get('/ai/check-available'),
  
  generateArticleDraft: (data) => api.post('/ai/generate-article-draft', data),
  
  generateCaption: (postId, options = {}) => 
    api.post(`/ai/posts/${postId}/generate-caption`, options),
  
  generateProductDescription: (postId, options = {}) => 
    api.post(`/ai/posts/${postId}/generate-product-description`, options),
  
  generateArticleContent: (postId, options = {}) => 
    api.post(`/ai/posts/${postId}/generate-article-content`, options),
  
  generateSEO: (postId, options = {}) => 
    api.post(`/ai/posts/${postId}/generate-seo`, options),
};

// =====================================================
// PUBLIC POSTS API (no authentication)
// =====================================================
export const publicPostsAPI = {
  getByToken: (token) => {
    // Create a separate axios instance without auth interceptor
    const publicApi = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return publicApi.get(`/posts/public/${token}`);
  },
  approveByToken: (token) => {
    const publicApi = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return publicApi.post(`/posts/public/${token}/approve`);
  },
  rejectByToken: (token, data) => {
    const publicApi = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return publicApi.post(`/posts/public/${token}/reject`, data);
  },
  addCommentByToken: (token, data) => {
    const publicApi = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return publicApi.post(`/posts/public/${token}/comments`, data);
  },
};

// =====================================================
// PUBLIC SEO API (no authentication)
// =====================================================
export const publicSeoAPI = {
  getByToken: (token) => {
    const publicApi = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return publicApi.get(`/seo/public/${token}`);
  },
  approveKeyword: (token, keywordId) => {
    const publicApi = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return publicApi.post(`/seo/public/${token}/keywords/${keywordId}/approve`);
  },
  rejectKeyword: (token, keywordId, data) => {
    const publicApi = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return publicApi.post(`/seo/public/${token}/keywords/${keywordId}/reject`, data);
  },
};

// =====================================================
// DESIGN REQUESTS API
// =====================================================
export const designRequestsAPI = {
  getAll: (params) => api.get('/design-requests', { params }),
  getById: (id) => api.get(`/design-requests/${id}`),
  create: (data) => api.post('/design-requests', data),
  update: (id, data) => api.patch(`/design-requests/${id}`, data),
  delete: (id) => api.delete(`/design-requests/${id}`),
  convertToPost: (id) => api.post(`/design-requests/${id}/convert-to-post`),
};

// =====================================================
// MEDIA API
// =====================================================
export const mediaAPI = {
  getAll: (params) => api.get('/media', { params }),
  getById: (id) => api.get(`/media/${id}`),
  upload: (files, folderId = null, clientId = null) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    if (folderId) {
      formData.append('folder_id', folderId);
    }
    if (clientId) {
      formData.append('client_id', clientId);
    }
    return api.post('/media/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  update: (id, data) => api.patch(`/media/${id}`, data),
  delete: (id) => api.delete(`/media/${id}`),
  bulkCreatePosts: (data) => api.post('/media/bulk-create-posts', data),
  // Folders
  getAllFolders: (params) => api.get('/media/folders', { params }),
  createFolder: (data) => api.post('/media/folders', data),
  updateFolder: (id, data) => api.patch(`/media/folders/${id}`, data),
  deleteFolder: (id) => api.delete(`/media/folders/${id}`),
};

// =====================================================
// COMMENTS API
// =====================================================
export const commentsAPI = {
  getByPost: (postId) => api.get(`/posts/${postId}/comments`),
  create: (postId, data) => api.post(`/posts/${postId}/comments`, data),
  update: (id, data) => api.patch(`/comments/${id}`, data),
  delete: (id) => api.delete(`/comments/${id}`),
};

// =====================================================
// NOTIFICATIONS API
// =====================================================
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  getCount: () => api.get('/notifications/count'),
  markAsRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllAsRead: () => api.post('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`),
  clearRead: () => api.delete('/notifications/clear-read'),
};

// =====================================================
// UPLOAD API
// =====================================================
export const uploadAPI = {
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// =====================================================
// USERS API
// =====================================================
// USERS API
// =====================================================
export const usersAPI = {
  getByRole: (role) => api.get(`/users/by-role?role=${role}`),
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.patch(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// =====================================================
// PLANS API
// =====================================================
export const plansAPI = {
  getAll: () => api.get('/plans'),
  getById: (id) => api.get(`/plans/${id}`),
  create: (data) => api.post('/plans', data),
  update: (id, data) => api.patch(`/plans/${id}`, data),
  delete: (id) => api.delete(`/plans/${id}`),
};

// =====================================================
// FEATURES API (Dynamic Features Management)
// =====================================================
export const featuresAPI = {
  getAll: () => api.get('/features'),
  getById: (id) => api.get(`/features/${id}`),
  create: (data) => api.post('/features', data),
  update: (id, data) => api.patch(`/features/${id}`, data),
  delete: (id) => api.delete(`/features/${id}`),
};

// =====================================================
// AGENCIES API
// =====================================================
export const agenciesAPI = {
  getAll: () => api.get('/agencies'),
  getById: (id) => api.get(`/agencies/${id}`),
  getMyAgency: () => api.get('/agencies/me'),
  create: (data) => api.post('/agencies', data),
  update: (id, data) => api.patch(`/agencies/${id}`, data),
  delete: (id) => api.delete(`/agencies/${id}`),
};

// =====================================================
// REPORTS API
// =====================================================
export const reportsAPI = {
  getStats: () => api.get('/reports/stats'),
  getActivity: (limit) => api.get(`/reports/activity${limit ? `?limit=${limit}` : ''}`),
};

// =====================================================
// SYSTEM SETTINGS API (Global settings like free trial)
// =====================================================
export const systemAPI = {
  getSettings: () => api.get('/system/settings'),
  updateSettings: (data) => api.put('/system/settings', data),
};

// =====================================================
// PACKAGES API (Agency Packages)
// =====================================================
export const packagesAPI = {
  // Agency Packages
  getAll: (config) => api.get('/packages', config || {}),
  getById: (id) => api.get(`/packages/${id}`),
  create: (data) => api.post('/packages', data),
  update: (id, data) => api.patch(`/packages/${id}`, data),
  delete: (id) => api.delete(`/packages/${id}`),
  
  // Client Packages
  getClientPackages: (clientId) => api.get(`/packages/client/${clientId}`),
  getAvailablePackages: (clientId, params) => api.get(`/packages/client/${clientId}/available`, { params }),
  assignToClient: (clientId, data) => api.post(`/packages/client/${clientId}`, data),
  createClientSpecific: (clientId, data) => api.post(`/packages/client/${clientId}/create-custom`, data),
  updateClientPackage: (id, data) => api.patch(`/packages/client-package/${id}`, data),
  removeFromClient: (id) => api.delete(`/packages/client-package/${id}`),
  usePackagePost: (id, postId) => api.post(`/packages/client-package/${id}/use`, { post_id: postId }),
};

// =====================================================
// TICKETS API
// =====================================================
export const ticketsAPI = {
  getAll: (params) => api.get('/tickets', { params }),
  getById: (id) => api.get(`/tickets/${id}`),
  create: (data) => api.post('/tickets', data),
  update: (id, data) => api.patch(`/tickets/${id}`, data),
  updateStatus: (id, data) => api.patch(`/tickets/${id}/status`, data),
  delete: (id) => api.delete(`/tickets/${id}`),
};

// =====================================================
// SOCIAL MEDIA API
// =====================================================
export const socialMediaAPI = {
  // Get all connections
  getConnections: () => api.get('/social-media/connections'),
  
  // Instagram
  getInstagramAuthUrl: () => api.get('/social-media/instagram/auth'),
  getInstagramAccountInfo: () => api.get('/social-media/instagram/account'),
  publishToInstagram: (data) => api.post('/social-media/instagram/publish', data),
  
  // Disconnect platform
  disconnect: (platform) => api.delete(`/social-media/${platform}/disconnect`),
  
  // TODO: Add other platforms when implemented
  // getTikTokAuthUrl: () => api.get('/social-media/tiktok/auth'),
  // getSnapchatAuthUrl: () => api.get('/social-media/snapchat/auth'),
  // getXAuthUrl: () => api.get('/social-media/x/auth'),
};

export default api;


